import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import passwordSuccessImage from '../../../assets/images/rocket-success.png';
import { TDButton, TDInputText, TDTypography } from '../../../atoms/TDUILib';
import AuthHeader from '../components/AuthHeader/AuthHeader';
import './NewPasswordPage.scss';
import useRecoverPassword from './useRecoverPassword';

const NewPasswordPage = ({ token }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [hasError, setHasError] = useState(false);
  const [password, setPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [passwordSent, setPasswordSent] = useState(false);
  const { sendNewPassword } = useRecoverPassword();
  const [showValidation, setShowValidation] = useState(false);

  // Add useEffect to clean URL parameters
  useEffect(() => {
    const cleanURL = () => {
      const url = window.location.pathname;
      window.history.replaceState({}, document.title, url);
    };
    cleanURL();
  }, []);

  // Validaciones
  const isPasswordTooShort = password.length < 8;
  const doPasswordsMatch = password === repeatPassword;

  // Mostrar validación cuando el usuario empiece a escribir
  const handlePasswordChange = ({ target: { value } }) => {
    setPassword(value);
    setShowValidation(true);
  };

  const handleRepeatPasswordChange = ({ target: { value } }) => {
    setRepeatPassword(value);
    setShowValidation(true);
  };

  // Botón deshabilitado si:
  // - los campos están vacíos
  // - las contraseñas no coinciden y el segundo campo tiene contenido
  // - la contraseña es muy corta y el campo tiene contenido
  const isButtonDisabled =
    password.length === 0 ||
    repeatPassword.length === 0 ||
    (repeatPassword.length > 0 && !doPasswordsMatch) ||
    (password.length > 0 && isPasswordTooShort);

  const handleSendNewPassword = async () => {
    // Validar antes de enviar
    if (isPasswordTooShort) {
      setHasError(true);
      return;
    }

    if (!doPasswordsMatch) {
      setHasError(true);
      return;
    }

    setIsLoading(true);
    setHasError(false);

    const response = await sendNewPassword({
      token,
      new_password: password
    });

    if (
      response?.status !== 'success' ||
      response?.status === 'error' ||
      response?.errorCode ||
      response?.data?.results?.errors?.length > 0
    ) {
      setHasError(true);
      setPasswordSent(false);
      console.log('🔥Error updating password');
    } else {
      setPasswordSent(true);
    }

    setIsLoading(false);
  };

  return (
    <div className='new-password-page__container'>
      {!passwordSent ? (
        <>
          <AuthHeader
            title={t('password_recovery_new_password_title')}
            subTitleVariant={'light'}
            className='new-password-page__header'
          />
          <div className='new-password-page__password-form'>
            <div className='new-password-page__content'>
              <TDInputText
                fullWidth
                disabled={isLoading}
                type='password'
                label={t('password_recovery_write_password')}
                placeholder={t('password')}
                value={password}
                inputProps={{ 'data-testid': 'new-password-input' }}
                onChange={handlePasswordChange}
                hasError={showValidation && isPasswordTooShort}
                errorText={
                  showValidation && isPasswordTooShort
                    ? t('password_recovery_error_too_short')
                    : ''
                }
                helperText={t('password_recovery_min_length')}
              />
              <TDInputText
                fullWidth
                disabled={isLoading}
                type='password'
                label={t('password_recovery_repeat_password')}
                placeholder={t('password')}
                value={repeatPassword}
                inputProps={{ 'data-testid': 'repeat-password-input' }}
                onChange={handleRepeatPasswordChange}
                hasError={showValidation && !doPasswordsMatch}
                errorText={
                  showValidation && !doPasswordsMatch
                    ? t('password_recovery_error_not_match')
                    : ''
                }
              />
            </div>
            <div className='new-password-page__password-form-footer'>
              <TDButton
                color='primary'
                fullWidth
                onClick={handleSendNewPassword}
                disabled={isButtonDisabled}
              >
                {t('password_recovery_create_password_button')}
              </TDButton>
            </div>
          </div>
        </>
      ) : (
        <>
          <AuthHeader className='new-password-page__header' />
          <div className='new-password-page__password-success'>
            <div className='new-password-page__content'>
              <div className='new-password-page__password-success-title'>
                <TDTypography as='div' variant='body-xl' fontWeight='semibold'>
                  {t('password_recovery_password_success_title')}
                </TDTypography>
              </div>
              <img
                src={passwordSuccessImage}
                alt='New password created successfully'
                className='new-password-page__success-image'
              />
              <div className='new-password-page__password-success-text'>
                <TDTypography as='div' variant='body-m' fontWeight='medium'>
                  {t('password_recovery_password_success_text')}
                </TDTypography>
              </div>
            </div>
            <div className='new-password-page__password-success-footer'>
              <TDButton
                color='primary'
                fullWidth
                onClick={() => navigate('/login')}
              >
                {t('password_recovery_enter_account')}
              </TDButton>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NewPasswordPage;
